import React, { lazy } from 'react';

// main routes
const DashboardPage = lazy(() => import('../pages/main/dashboard'));
const Companies = lazy(() => import('../pages/main/estate-management/company'));
const Estates = lazy(() => import('../pages/main/estate-management/estates'));
const PropertyManagement = lazy(
  () => import('../pages/main/estate-management/property-management')
);
const BillingItems = lazy(
  () => import('../pages/main/billing-management/billing-items')
);
const BillingTemplates = lazy(
  () => import('../pages/main/billing-management/billing-templates')
);
const InvoiceTemplate = lazy(
  () => import('../pages/main/billing-management/invoice')
);
const VendingUnit = lazy(
  () => import('../pages/main/vending-management/vending-unit')
);
const VendingCommonArea = lazy(
  () => import('../pages/main/vending-management/vending-common-area')
);
const WalletFunding = lazy(
  () => import('../pages/main/vending-management/wallet-funding')
);
const EmergencyToken = lazy(
  () => import('../pages/main/vending-management/emergency-token')
);
const TamperCode = lazy(
  () => import('../pages/main/vending-management/tamper-code')
);
const VisitorsLogs = lazy(
  () => import('../pages/main/visitors-management/visitors-logs')
);
const Securitystaffs = lazy(
  () => import('../pages/main/visitors-management/security-staffs')
);
const Reports = lazy(() => import('../pages/main/reports'));
const TransactionHistory = lazy(
  () => import('../pages/main/unit-transaction-history')
);
const Settings = lazy(() => import('../pages/main/settings'));
const UserManagement = lazy(() => import('../pages/main/user-management'));
const UserSupport = lazy(() => import('../pages/main/support'));
const TicketDetails = lazy(() => import('../pages/main/support/ticket-details'));

// auth routes
const AuthPage = lazy(() => import('../pages/auth'));
const LoginPage = lazy(() => import('../pages/auth/login'));
const LogoutPage = lazy(() => import('../pages/auth/logout'));
const ForgotPassword = lazy(() => import('../pages/auth/forgot-passwrod'));
const ConfirmOtp = lazy(() => import('../pages/auth/confirm-otp'));
const ResetPassword = lazy(() => import('../pages/auth/reset-password'));
const CreatePassword = lazy(() => import('../pages/auth/create-password'));
const NotFoundPage = lazy(() => import('../pages/auth/404'));

export type RouteProps = {
  path: string;
  label: string;
  description?: string;
  roles?: 'ADMIN' | 'USER';
  component: React.ReactComponentElement<never> | React.ReactElement;
};

export const routes: RouteProps[] = [
  // main pages with no initial path >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/',
    label: 'Dashboard',
    component: <DashboardPage />,
    description: 'Welcome back to Estate Plus 👋🏻',
  },
  // Estate manegement >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/companies',
    label: 'Manage Companies',
    component: <Companies />,
    description: 'View and manage all companies',
  },
  {
    path: '/estates',
    label: 'Manage Estates',
    component: <Estates />,
    description: 'View and manage all estates',
  },
  {
    path: '/properties',
    label: 'Property Units',
    component: <PropertyManagement />,
    description: 'View and manage all property units',
  },
  {
    path: '/wallet-fund',
    label: 'Wallet Funding',
    component: <WalletFunding />,
  },
  // Billing manegement >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/bill-items',
    label: 'Billing Items',
    component: <BillingItems />,
    description:
      'Create and manage all billing items here, and reuse them when generating bills',
  },
  {
    path: '/bill-templates',
    label: 'Billing Templates',
    component: <BillingTemplates />,
  },
  {
    path: '/bill-invoices',
    label: 'Invoices',
    component: <InvoiceTemplate />,
  },
  // vending manegement >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/vend-unit',
    label: 'Vending (Property Unit)',
    component: <VendingUnit />,
  },
  {
    path: '/vend-area',
    label: 'Vending (Common Area)',
    component: <VendingCommonArea />,
  },
  {
    path: '/emergency-token',
    label: 'Emergency Token',
    component: <EmergencyToken />,
  },
  {
    path: '/tamper-code',
    label: 'Tamper Code',
    component: <TamperCode />,
  },
  // visitors management >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/visitor-logs',
    label: 'Visitors Logs',
    component: <VisitorsLogs />,
  },
  {
    path: '/security-staffs',
    label: 'Security Staffs',
    component: <Securitystaffs />,
  },
  // reports >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/reports',
    label: 'Reports',
    component: <Reports />,
  },
  // transaction history >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/transactions',
    label: 'Unit Transaction History',
    component: <TransactionHistory />,
  },
  // User Management >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/users-mgt',
    label: 'Users Management',
    component: <UserManagement />,
  },
  // settings >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/settings',
    label: 'Settings',
    component: <Settings />,
    description: 'Manage your preferences and configure various options.',
  },
  // support and complaints >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '/support',
    label: 'Support and Complaint Management',
    component: <UserSupport />,
    description: 'Manage user compalints and ticketing.',
  },
  {
    path: '/support/ticket/:ticket_id',
    label: 'Ticket Details',
    component: <TicketDetails />,
    description: 'View and manage ticket details.',
  },

  // auth pages with auth initial path >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  {
    path: '*',
    label: 'Not Found',
    component: <NotFoundPage />,
  },
  {
    path: '/auth',
    label: 'Auth Page',
    component: <AuthPage />,
  },
  {
    path: '/auth/login',
    label: 'Login Page',
    component: <LoginPage />,
  },
  {
    path: '/auth/logout',
    label: 'Logout Page',
    component: <LogoutPage />,
  },
  {
    path: '/auth/forgot-password',
    label: 'Forgot Password',
    component: <ForgotPassword />,
  },
  {
    path: '/auth/confirm-otp',
    label: 'Confirm OTP',
    component: <ConfirmOtp />,
  },
  {
    path: '/auth/reset-password',
    label: 'Reset Password',
    component: <ResetPassword />,
  },
  {
    path: '/auth/create-password',
    label: 'Create Password',
    component: <CreatePassword />,
  },
];
